/**
 * Dobór koloru page: upload a photo or take a selfie, confirm the thumbnail,
 * then run the shared shade analyzer (VivaShade) to show the closest shades.
 */
const initializedVivaColorMatch = new WeakSet();
const initVivaColorMatch = () => {
  const root = document.querySelector('[data-cmatch]');
  if (!root || !window.VivaShade) return;
  if (initializedVivaColorMatch.has(root)) return;
  initializedVivaColorMatch.add(root);
  let content = {};
  try { content = JSON.parse(root.dataset.toolContent || '{}'); } catch (error) { content = {}; }

  const pick = root.querySelector('[data-cmatch-pick]');
  const preview = root.querySelector('[data-cmatch-preview]');
  const input = root.querySelector('[data-cmatch-input]');
  const selfieBtn = root.querySelector('[data-cmatch-selfie]');
  const camera = root.querySelector('[data-cmatch-camera]');
  const video = root.querySelector('[data-cmatch-video]');
  const captureBtn = root.querySelector('[data-cmatch-capture]');
  const cancelBtn = root.querySelector('[data-cmatch-cancel]');
  const chosen = root.querySelector('[data-cmatch-chosen]');
  const image = root.querySelector('[data-cmatch-image]');
  const analyzeBtn = root.querySelector('[data-cmatch-analyze]');
  const reset = root.querySelector('[data-cmatch-reset]');
  const work = root.querySelector('[data-cmatch-work]');
  const media = root.querySelector('[data-cmatch-media]');
  const intro = root.querySelector('[data-cmatch-intro]');
  const restartButtons = Array.from(root.querySelectorAll('[data-cmatch-restart]'));
  const title = root.querySelector('[data-cmatch-title]');
  const initialTitle = title ? title.textContent : '';
  const progressRoot = root.querySelector('[data-cmatch-progress]');
  const progressElapsed = root.querySelector('[data-cmatch-progress-elapsed]');
  const progressLabel = root.querySelector('[data-cmatch-progress-label]');
  const results = root.querySelector('[data-cmatch-results]');
  const unavailable = root.querySelector('[data-cmatch-unavailable]');
  const list = root.querySelector('[data-cmatch-list]');
  if (!input || !image) return;

  let stream = null;
  let bestMatch = null;
  let photoObjectUrl = '';
  const releasePhoto = () => {
    if (photoObjectUrl) URL.revokeObjectURL(photoObjectUrl);
    photoObjectUrl = '';
    image.removeAttribute('src');
  };
  const progress =
    window.VivaAsyncProgress && progressRoot
      ? window.VivaAsyncProgress.create({
          root: progressRoot,
          labelEl: progressLabel,
          elapsedEl: progressElapsed,
          doneLabel: content.progress_done,
          stageCaps: {
            queued: 0.3,
            running: 0.6,
            segmenting_hair: 0.6,
            matching_reference: 0.9,
            fallback: 0.85,
          },
          stageLabels: {
            preflight: content.progress_queued,
            uploading: content.progress_queued,
            queued: content.progress_queued,
            running: content.progress_running,
            segmenting_hair: content.progress_running,
            matching_reference: content.progress_matching,
            fallback: content.progress_fallback,
          },
        })
      : null;

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    if (video instanceof HTMLVideoElement) video.srcObject = null;
  };

  const closeCamera = () => {
    stopStream();
    if (camera) camera.hidden = true;
  };

  const setChosen = (src) => {
    releasePhoto();
    if (src.startsWith('blob:')) photoObjectUrl = src;
    let revealed = false;
    const revealChosen = () => {
      if (revealed) return;
      revealed = true;
      window.VivaPhotoSession?.save(image);
      closeCamera();
      if (media) media.hidden = false;
      if (preview) preview.hidden = false;
      if (pick) pick.hidden = true;
      if (intro) intro.hidden = true;
      if (chosen) chosen.hidden = false;
      if (work) work.hidden = false;
      if (results) results.hidden = true;
      if (unavailable) unavailable.hidden = true;
      if (progress) progress.reset();
      if (analyzeBtn) analyzeBtn.textContent = content.analyze_label;
      if (list) list.innerHTML = '';
      image.onload = null;
      image.onerror = null;
    };

    image.onload = revealChosen;
    image.onerror = revealChosen;
    image.src = src;
    if (image.complete && image.naturalWidth) {
      revealChosen();
    }
  };

  const resetAll = () => {
    window.VivaPhotoSession?.clear();
    closeCamera();
    releasePhoto();
    bestMatch = null;
    input.value = '';
    if (progress) progress.reset();
    if (media) media.hidden = true;
    if (preview) preview.hidden = true;
    if (chosen) chosen.hidden = true;
    if (intro) intro.hidden = false;
    if (pick) pick.hidden = false;
    if (results) results.hidden = true;
    if (unavailable) unavailable.hidden = true;
    if (work) work.hidden = false;
    if (list) list.innerHTML = '';
    if (title) title.textContent = initialTitle;
    if (analyzeBtn) analyzeBtn.textContent = content.analyze_label;
  };

  // --- Upload ---
  input.addEventListener('change', () => {
    const file = input.files && input.files[0];
    if (!file) return;
    setChosen(URL.createObjectURL(file));
  });

  // --- Selfie ---
  const supportsCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

  if (selfieBtn) {
    if (supportsCamera) {
      selfieBtn.addEventListener('click', async () => {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
          if (video instanceof HTMLVideoElement) {
            video.srcObject = stream;
            await video.play().catch(() => {});
          }
          if (media) media.hidden = false;
          if (camera) camera.hidden = false;
          if (pick) pick.hidden = true;
        } catch (error) {
          input.setAttribute('capture', 'user');
          input.click();
        }
      });
    } else {
      selfieBtn.addEventListener('click', () => {
        input.setAttribute('capture', 'user');
        input.click();
      });
    }
  }

  if (captureBtn) {
    captureBtn.addEventListener('click', () => {
      if (!(video instanceof HTMLVideoElement) || !video.videoWidth) return;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setChosen(canvas.toDataURL('image/jpeg', 0.92));
    });
  }

  if (cancelBtn) {
    cancelBtn.addEventListener('click', () => {
      closeCamera();
      if (media) media.hidden = true;
      if (pick) pick.hidden = false;
    });
  }

  // --- Analyse ---
  if (analyzeBtn) {
    analyzeBtn.addEventListener('click', () => {
      if (chosen) chosen.hidden = true; // the progress ring is the indicator now
      if (progress) progress.start('queued');
      window.VivaShade.rank(image, (state) => {
        if (progress) progress.update(state);
      })
        .then((ranked) => {
          window.VivaShade.render(list, ranked, content);
          bestMatch = ranked && ranked[0] ? ranked[0].code : null;
          if (bestMatch) window.VivaAnalytics?.publish('color_match_completed', { shade_code: bestMatch });
          if (progress) progress.finish();
          // Let the ring reach 100%, then swap the work area for the results.
          window.setTimeout(() => {
            if (work) work.hidden = true;
            if (results) results.hidden = false;
            if (title) title.textContent = content.results_title;
          }, 700);
        })
        .catch(() => {
          if (progress) progress.reset();
          if (work) work.hidden = true;
          if (results) results.hidden = true;
          if (unavailable) unavailable.hidden = false;
          if (title) title.textContent = content.unavailable_label;
        });
    });
  }

  window.addEventListener('pagehide', closeCamera);
  if (reset) reset.addEventListener('click', resetAll);
  restartButtons.forEach((button) => button.addEventListener('click', resetAll));

  // Carry the selected shade alongside the shared, tab-scoped source photo.
  const tryonLink = root.querySelector('[data-cmatch-tryon]');
  if (tryonLink) {
    tryonLink.addEventListener('click', () => {
      try {
        if (bestMatch) sessionStorage.setItem('viva:tryon-shade', bestMatch);
        else sessionStorage.removeItem('viva:tryon-shade');
      } catch (error) { /* storage unavailable */ }
    });
  }

  const restorePhoto = () => {
    const saved = window.VivaPhotoSession?.read();
    if (saved) setChosen(saved);
  };
  restorePhoto();
  window.addEventListener('pageshow', event => { if (event.persisted) restorePhoto(); });
};
document.addEventListener('DOMContentLoaded', initVivaColorMatch);
document.addEventListener('shopify:section:load', initVivaColorMatch);
